import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { DocumentRenderer } from "../components/DocumentRenderer";
import { SecurityReportForm } from "../components/SecurityReportForm";
import securityContent from "../../../docs/security_policy.md";

export function SecurityPage() {
  return (
    <div className="no-print bg-white dark:bg-[#090e17] min-h-screen transition-colors">
      {/* Hero Banner */}
      <PageHero
        badge="RADIECE Security & Trust"
        title="Security & Bug Reporting"
        subtitle="Responsible Disclosure & Incident Response"
        description="Our engineering team prioritises operational security, rapid bug triaging, responsible vulnerability disclosure, and statutory data protection compliance."
        maxWidth="max-w-5xl"
      >
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="#report-form"
          className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 font-medium hover:bg-white/20 transition-colors"
        >
          Submit Report Form
        </motion.a>
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="#security-policy"
          className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 font-medium hover:bg-white/20 transition-colors"
        >
          Read Disclosure Policy
        </motion.a>
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="mailto:security@radiece.com"
          className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 font-medium hover:bg-white/20 transition-colors"
        >
          Direct Security Email
        </motion.a>
      </PageHero>

      {/* Main Interactive Form & Info Grid */}
      <section id="report-form" className="no-print bg-slate-50 dark:bg-[#070b12] py-16 sm:py-20 border-b border-slate-200/80 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] items-start">
            {/* Left Info Panel */}
            <div className="space-y-6">
              <div>
                <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Security Portal</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight mb-4">
                  Report a security finding or bug.
                </h2>
                <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                  We welcome responsible disclosures from independent researchers, clients, and developers. Submissions via this portal are triaged directly by our technical security team.
                </p>
              </div>

              <div className="bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm space-y-4 transition-colors">
                <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Response Timelines (SLAs)
                </h3>
                <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span><strong>24 Hours:</strong> Initial report acknowledgment and triage.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span><strong>72 Hours:</strong> Vulnerability assessment & severity classification.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span><strong>24–48 Hours:</strong> Patch deployment for Critical / High findings.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm transition-colors">
                <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">Direct Contact Backup</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  If you prefer PGP-encrypted communication or direct email transmission:
                </p>
                <p className="text-sm font-mono text-brand dark:text-primary font-semibold">
                  security@radiece.com
                </p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  For data breach alerts, please include "[URGENT BREACH]" in the subject line.
                </p>
              </div>
            </div>

            {/* Right Form Card */}
            <div>
              <SecurityReportForm />
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Statutory Policy Document */}
      <section id="security-policy" className="no-print bg-white dark:bg-[#0b131e] py-16 sm:py-20 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Statutory Policy & Guidelines</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Responsible Disclosure & Incident Directives
            </h2>
          </div>

          <div className="bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-10 shadow-sm doc-section transition-colors">
            <DocumentRenderer content={securityContent} />
          </div>
        </div>
      </section>
    </div>
  );
}
